/*
Copyright (c) (2022 - infinity) Maifee Ul Asad
*/

import React, { useMemo, useState } from 'react';

import { type FluidPath, type PathPoint, getPredefinedPaths } from './paths';

interface PathManagerProps {
  onPathChange: (path: FluidPath | null) => void;
}

const PathManager: React.FC<PathManagerProps> = ({ onPathChange }) => {
  const [selectedPath, setSelectedPath] = useState<FluidPath | null>(null);
  const [customPaths, setCustomPaths] = useState<FluidPath[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPoints, setRecordingPoints] = useState<PathPoint[]>([]);
  const [recordingStartTime, setRecordingStartTime] = useState<number>(0);
  const [showManager, setShowManager] = useState(false);
  const predefinedPaths = useMemo(() => getPredefinedPaths(), []);

  const handlePathSelect = (path: FluidPath) => {
    setSelectedPath(path);
    onPathChange(path);
  };

  const handleStopPath = () => {
    setSelectedPath(null);
    onPathChange(null);
  };

  const recordDragMove = (e: MouseEvent | TouchEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    let clientX: number, clientY: number;

    if (e instanceof TouchEvent) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const normalizedX = x / rect.width;
    const normalizedY = y / rect.height;
    const timestamp = (Date.now() - recordingStartTime) / 1000; // seconds since recording started

    setRecordingPoints(prev => [...prev, { x: normalizedX, y: normalizedY, timestamp }]);
  };

  const startRecording = () => {
    setRecordingPoints([]);
    setRecordingStartTime(Date.now());

    window.addEventListener('mousemove', recordDragMove as EventListener);
    window.addEventListener('touchmove', recordDragMove as EventListener);

    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recordingPoints.length > 0) {
      const newPath: FluidPath = {
        id: `custom-${Date.now()}`,
        name: `Custom Path ${customPaths.length + 1}`,
        points: recordingPoints,
        duration: (Date.now() - recordingStartTime) / 1000,
        loop: false,
      };
      setCustomPaths([...customPaths, newPath]);
    }
    setIsRecording(false);
    setRecordingPoints([]);

    window.removeEventListener('mousemove', recordDragMove as EventListener);
    window.removeEventListener('touchmove', recordDragMove as EventListener);
  };

  const deleteCustomPath = (pathId: string) => {
    setCustomPaths(customPaths.filter(p => p.id !== pathId));
    if (selectedPath?.id === pathId) {
      handleStopPath();
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setShowManager(!showManager)}
        style={{
          position: 'fixed',
          top: '100px',
          right: '20px',
          zIndex: 2000,
          padding: '12px',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          backdropFilter: 'blur(10px)',
        }}
      >
        {showManager ? '✕ Close' : '🎯 Paths'}
      </button>

      {/* Path Manager Panel */}
      {showManager && (
        <div
          style={{
            position: 'fixed',
            top: '140px',
            right: '20px',
            width: '280px',
            maxHeight: '60vh',
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(15px)',
            borderRadius: '12px',
            padding: '20px',
            color: 'white',
            zIndex: 1500,
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem' }}>Path Manager</h3>

          {/* Current Status */}
          <div style={{ marginBottom: '15px', padding: '10px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
            <strong>Status: </strong>
            {selectedPath ? (
              <span style={{ color: '#4fc3f7' }}>{`Following "${selectedPath.name}"`}</span>
            ) : isRecording ? (
              <span style={{ color: '#f44336' }}>Recording path...</span>
            ) : (
              <span style={{ color: '#9e9e9e' }}>Free mode (mouse/touch)</span>
            )}
          </div>

          {/* Control Buttons */}
          <div style={{ marginBottom: '20px' }}>
            {selectedPath ? (
              <button
                onClick={handleStopPath}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginBottom: '10px',
                }}
              >
                Stop Following Path
              </button>
            ) : (
              <div>
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginBottom: '10px',
                    }}
                  >
                    🔴 Record New Path
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginBottom: '10px',
                    }}
                  >
                    ⏹️ Stop Recording
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Predefined Paths */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#4fc3f7' }}>Predefined Paths</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {predefinedPaths.map(path => (
                <button
                  key={path.id}
                  onClick={() => handlePathSelect(path)}
                  disabled={isRecording}
                  style={{
                    padding: '8px',
                    background: selectedPath?.id === path.id ? '#4fc3f7' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isRecording ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: isRecording ? 0.5 : 1,
                  }}
                >
                  {path.name}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Paths */}
          {customPaths.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', color: '#4fc3f7' }}>Custom Paths</h4>
              {customPaths.map(path => (
                <div
                  key={path.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px',
                    background: selectedPath?.id === path.id ? '#4fc3f7' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    marginBottom: '6px',
                  }}
                >
                  <button
                    onClick={() => handlePathSelect(path)}
                    disabled={isRecording}
                    style={{
                      flex: 1,
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: isRecording ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                      opacity: isRecording ? 0.5 : 1,
                    }}
                  >
                    {path.name}
                  </button>
                  <button
                    onClick={() => deleteCustomPath(path.id)}
                    style={{
                      background: '#f44336',
                      border: 'none',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                      padding: '4px 6px',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Instructions */}
          <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '15px', lineHeight: '1.4' }}>
            <li style={{ margin: '0 0 5px 0' }}>Click a path to make fluid follow it</li>
            <li style={{ margin: '0 0 5px 0' }}>Record custom paths by dragging mouse/finger</li>
            <li style={{ margin: '0' }}>Custom paths are saved for this session</li>
          </div>
        </div>
      )}
    </>
  );
};

export default PathManager;
